import React, { useRef, useState } from "react";
import { Alert, Button, Card, Form, Input } from "antd";
import { Link } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import '../assets/css/Dentobook.css';
import { CloseOutlined } from "@ant-design/icons";
import { isMobileOnly } from "react-device-detect";
import { getAuthenticationApi } from "../api/apiInstances";
import TurnstileWidget, { TurnstileHandle, TURNSTILE_ENABLED } from "../components/common/TurnstileWidget";

const ForgotPassword = () => {
    const intl = useIntl();
    const [email, setEmail] = useState<string>('');
    const [errMsg, setErrMsg] = useState<string>('');
    const [successMsg, setSuccessMsg] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const turnstileRef = useRef<TurnstileHandle>(null);
    const submittingRef = useRef(false);

    const [form] = Form.useForm();

    const onFinish = async () => {
        if (submittingRef.current) {
            return;
        }

        if (TURNSTILE_ENABLED && !turnstileToken) {
            setErrMsg(intl.formatMessage({id: 'complete_bot_verification'}));
            return;
        }

        submittingRef.current = true;
        setLoading(true);
        setErrMsg('');
        setSuccessMsg('');

        const capturedToken = turnstileToken;
        setTurnstileToken(null);

        try {
            // Call forgot password endpoint using generated API client
            const authApi = getAuthenticationApi();
            const response = await authApi.requestPasswordReset({
                email: email.trim().toLowerCase(),
                turnstileToken: capturedToken || undefined,
            });

            setSuccessMsg(response.data.message || intl.formatMessage({ id: 'password_reset_link_sent' }));

            // Clear the form
            form.resetFields();
            setEmail('');
            turnstileRef.current?.reset();

        } catch (err: any) {
            if (err.response?.data?.detail) {
                setErrMsg(err.response.data.detail);
            } else {
                setErrMsg(intl.formatMessage({ id: 'unexpected_error' }));
            }
            turnstileRef.current?.reset();
        } finally {
            submittingRef.current = false;
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                overflow: "hidden",
            }}
        >
            <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <a href="https://dentobook.com/en/" target="_blank" rel="noopener noreferrer">
                    <img
                        alt="logo"
                        className="dentobook-logo"
                        src={require("../assets/logo.png")}
                        style={{
                            width: "420px",
                            height: "104px",
                            padding: "13px 10px 13.65px 10px",
                        }}
                    />
                </a>
            </div>
            <Card
                style={{
                    width: isMobileOnly ? "90vw" : 480,
                    padding: "40px",
                    borderRadius: "30px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
                }}
            >
                <div style={{ marginBottom: "24px", textAlign: "center" }}>
                    <h2 style={{ marginBottom: "8px", fontSize: "24px", fontWeight: 600 }}>
                        <FormattedMessage id="forgot_password_title" />
                    </h2>
                    <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
                        <FormattedMessage id="forgot_password_description" />
                    </p>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    style={{ textAlign: "left" }}
                >
                    {successMsg ? (
                        <Alert
                            message={successMsg}
                            type="success"
                            className="mb-24"
                            closeIcon={<CloseOutlined />}
                            onClose={() => setSuccessMsg('')}
                            showIcon
                            onClick={() => setSuccessMsg('')}
                        />
                    ) : null}

                    {errMsg ? (
                        <Alert
                            message={errMsg}
                            type="error"
                            className="mb-24"
                            closeIcon={<CloseOutlined />}
                            onClose={() => setErrMsg('')}
                            showIcon
                            onClick={() => setErrMsg('')}
                        />
                    ) : null}

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="email" />}
                        name="email"
                        rules={[
                            { required: true, message: intl.formatMessage({ id: 'email_required_error' }) },
                            { type: "email", message: intl.formatMessage({ id: 'invalid_email' }) }
                        ]}
                    >
                        <Input
                            style={{ borderRadius: "12px", height: "48px" }}
                            autoComplete="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder={intl.formatMessage({ id: 'email_placeholder' })}
                        />
                    </Form.Item>

                    <TurnstileWidget
                        ref={turnstileRef}
                        onVerify={setTurnstileToken}
                        onExpire={() => setTurnstileToken(null)}
                    />

                    <div className="dentobook-register-btn">
                        <Form.Item className={"dentobook-form-item dentobook-simple-input"}>
                            <Button
                                className="dentobook-btn-primary dentobook-btn-medium"
                                type="primary"
                                htmlType="submit"
                                disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)}
                                style={{
                                    width: "100%",
                                    height: "48px",
                                }}
                            >
                                {loading ? <FormattedMessage id="sending" /> : <FormattedMessage id="send_reset_link" />}
                            </Button>
                        </Form.Item>
                    </div>

                    <div style={{ textAlign: "center", marginTop: "16px" }}>
                        <Link to="/login" style={{ color: "#1890ff", fontWeight: 500 }}>
                            <FormattedMessage id="back_to_login" />
                        </Link>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default ForgotPassword;
